import { Skeleton } from '@material-ui/lab';
import {
  AvatarButton,
  InfoCard,
  Inline,
  Stack,
  useSnackbarStack,
} from '@superdispatch/ui';
import { Box } from '@superdispatch/ui-lab';
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { MutationDialog } from 'shared/form/MutationDialog';
import { useCustomerSupport } from 'shared/helpers/CustomerSupport';
import { useQuery } from 'shared/helpers/RoutingHelpers';
import { useDocumentTitle } from 'shared/layout/DocumentTitle';
import {
  useDeleteNegativeRating,
  useLatestShipperRating,
  useShipperRatingsCache,
} from 'shared/modules/ratings/data/ShipperRatingsAPI';
import { useShipperProfile } from 'shared/modules/shipper/LoadboardShipperAPI';
import { shipperProfilePageParamsSchema } from 'shared/modules/shipper/ShipperProfilePageParams';
import { useLocationParams } from 'shared/routing/LocationParams';
import { ScrollToTop } from 'shared/routing/ScrollToTop';
import { ConfirmDialogContent } from 'shared/ui/ConfirmDialog';
import { trackLoadboardEvent } from '../LoadboardAnalytics';
import { useShipperProfileAnalytics } from './data/ShipperProfileAnalytics';
import { ShipperProfileHeader } from './ShipperProfileHeader';
import { ShipperProfileOverview } from './ShipperProfileOverview';
import { ShipperProfileRatings } from './ShipperProfileRatings';
import { ShipperProfileHeaderRating } from './ShipperProfileHeaderRating';
import { ShipperRatingDrawer } from 'shared/modules/ratings/ShipperRatingDrawer';

const isLoadboardMobile = import.meta.env.VITE_APP_NAME === 'slbm';

interface ShipperProfileProps {
  guid: string;
}

export function ShipperProfile({ guid }: ShipperProfileProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { addSnackbar } = useSnackbarStack();
  const containerRef = useRef<HTMLElement>(null);
  const { showLauncherMessanger } = useCustomerSupport();

  const { data: shipperProfile, error: shipperProfileError } =
    useShipperProfile(guid);
  const { data: latestShipperRating } = useLatestShipperRating(guid);
  const trackShipperEvent = useShipperProfileAnalytics(shipperProfile);
  const { invalidateShipperRatings } = useShipperRatingsCache();

  const [query, setQuery] = useQuery();
  const [params] = useLocationParams({
    yupSchema: shipperProfilePageParamsSchema,
    persistName: 'ShipperProfile',
  });

  const [isRatingFormOpen, setIsRatingFormOpen] = useState(false);
  const [isDeleteRatingDialogOpen, setIsDeleteRatingDialogOpen] =
    useState(false);

  const deleteNegativeRating = useDeleteNegativeRating({
    onSuccess: () => {
      addSnackbar('Rating deleted', { variant: 'success' });
      invalidateShipperRatings();
      setIsDeleteRatingDialogOpen(false);
    },
    onError: (error) => {
      addSnackbar(
        error.message === 'Forbidden'
          ? 'No rating permission. Contact manager to resolve the issue.'
          : error.message || 'Cannot delete rating',
        { variant: 'error' },
      );
    },
  });

  const onClickDeleteRating = () => {
    setIsDeleteRatingDialogOpen(true);
  };

  const onCloseDeleteRatingDialog = () => {
    setIsDeleteRatingDialogOpen(false);
  };

  const onClickRate = () => {
    if (isLoadboardMobile) {
      navigate({ pathname: `/rate-shipper/${guid}/`, search: location.search });
    } else {
      setIsRatingFormOpen(true);
    }
  };

  useEffect(() => {
    if (query.open_rating_dialog) {
      setIsRatingFormOpen(true);
      setQuery({ open_rating_dialog: undefined });
    }
  }, [query, setQuery]);

  useEffect(() => {
    if (query.show_intercom_revoked_message) {
      showLauncherMessanger();
      setQuery({ show_intercom_revoked_message: undefined });
    }
  }, [setQuery, query, showLauncherMessanger]);

  useEffect(() => {
    if (query.utm_medium !== 'Shipper Profile') {
      trackShipperEvent('SLB: Viewed Shipper Profile');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackShipperEvent]);

  useEffect(() => {
    if (shipperProfileError) {
      addSnackbar('Failed to fetch shipper profile', { variant: 'error' });
    }
  }, [addSnackbar, shipperProfileError]);

  useEffect(() => {
    // Tracks the initial page opening from outside the shipper profile page
    if (query.tab === 'ratings' && query.utm_medium !== 'Shipper Profile') {
      trackLoadboardEvent({ name: 'SLB: Opened Shipper’s Ratings' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDocumentTitle('Shipper Profile');

  if (!shipperProfile) {
    return (
      <Box padding={['small', 'medium']}>
        <Box maxWidth="960px" margin="auto">
          <Stack space="small">
            <InfoCard>
              <Inline space="small" verticalAlign="center">
                <AvatarButton size="large" />

                <Skeleton width="200px" height="44px" />
              </Inline>
            </InfoCard>

            <InfoCard>
              <Skeleton width="200px" height="32px" />

              <Skeleton width="300px" height="32px" />
            </InfoCard>
          </Stack>
        </Box>
      </Box>
    );
  }

  return (
    <>
      <ScrollToTop
        key={params.page}
        // need to fix <LoadboardDesktopLayout/> to remove this hack
        anchorElement={isLoadboardMobile ? undefined : containerRef.current}
      />

      <ShipperRatingDrawer
        shipperGUID={guid}
        open={isRatingFormOpen}
        onClose={() => {
          setIsRatingFormOpen(false);
        }}
      />

      <MutationDialog
        open={isDeleteRatingDialogOpen}
        onClose={onCloseDeleteRatingDialog}
        maxWidth="xs"
      >
        <ConfirmDialogContent
          onClose={onCloseDeleteRatingDialog}
          title="Delete rating?"
          cancelButtonProps={{ disabled: deleteNegativeRating.isPending }}
          confirmButtonProps={{
            children: 'Delete',
            pending: deleteNegativeRating.isPending,
            onClick() {
              deleteNegativeRating.mutate(guid);
            },
          }}
        >
          <Box width="312px" />
        </ConfirmDialogContent>
      </MutationDialog>

      <Box ref={containerRef} padding={['small', 'medium']}>
        <Box maxWidth="960px" margin="auto">
          <Stack space="small">
            <ShipperProfileHeader
              profile={shipperProfile}
              activeTab={params.tab}
              ratingSection={
                <ShipperProfileHeaderRating
                  shipperProfile={shipperProfile}
                  onClickDeleteRating={onClickDeleteRating}
                  onClickRate={onClickRate}
                />
              }
            />

            {params.tab === 'ratings' ? (
              <ShipperProfileRatings
                profile={shipperProfile}
                latestShipperRating={latestShipperRating}
                params={params}
                updateParams={setQuery}
                onClickRate={onClickRate}
                onClickDeleteRating={onClickDeleteRating}
              />
            ) : (
              <ShipperProfileOverview data={shipperProfile} />
            )}
          </Stack>
        </Box>
      </Box>
    </>
  );
}
